import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Filter,
  LineChart,
  MessagesSquare,
  ShieldCheck,
  UserCheck,
  UserPlus,
  UserRound,
  Video,
} from "lucide-react";
import PageSeo from "../components/PageSeo";
import Nd1SiteNav from "./components/Nd1SiteNav";
import Nd1SiteFooter from "./components/Nd1SiteFooter";
import FlowStep from "./components/FlowStep";
import Nd1DemoVideoPlaceholder from "./components/Nd1DemoVideoPlaceholder";
import Nd1FlowBreakdown from "./components/Nd1FlowBreakdown";
import { HIRING_HOW_IT_WORKS_VIDEO_ID } from "../lib/youtubeVideos";
import { buildBreadcrumbJsonLd, buildPublicPageUrl } from "../lib/pageSeo";
import { ND1_HIW_HIRING } from "./nd1Paths";
import "./nd1.css";

export default function Nd1HowItWorksHiringPage() {
  const pageUrl = buildPublicPageUrl(ND1_HIW_HIRING);

  return (
    <div className="nd1-root nd1-root--hiring">
      <PageSeo
        title="How it works — Hiring"
        description="See exactly how hiring on JOBUWIN works: post a job in minutes, get genuine verified applicants, watch video pitches, and shortlist with confidence."
        path={ND1_HIW_HIRING}
        canonicalPath={ND1_HIW_HIRING}
        schemaType="website"
        jsonLd={buildBreadcrumbJsonLd([
          { name: "Home", url: buildPublicPageUrl("/") },
          { name: "How it works — Hiring", url: pageUrl },
        ])}
      />

      <a href="#nd1-main" className="nd1-skip-link">
        Skip to content
      </a>

      <Nd1SiteNav />

      <main id="nd1-main">
        <section className="nd1-shell nd1-section">
          <div className="nd1-eyebrow nd1-ink-orange">How it works · For hiring</div>
          <h1 className="nd1-js-hero__title">
            Hire <span className="nd1-ink-orange">genuine people</span> — no AI spin.
          </h1>

          <div className="nd1-section nd1-section--tight">
            <Nd1DemoVideoPlaceholder
              slotId="hiring-journey"
              playLabel="Play the hiring demo"
              videoId={HIRING_HOW_IT_WORKS_VIDEO_ID}
              thumbnailAlt="How hiring on JOBUWIN works"
              accent
            />

            <div className="nd1-section nd1-section--tight">
              <Nd1FlowBreakdown>
                <FlowStep
                  icon={ShieldCheck}
                  title="Stand out as a genuine company"
                  description="Every employer is reviewed before jobs go live — so applicants know you're real."
                />
                <FlowStep
                  icon={Briefcase}
                  title="Post a job opening"
                  description="Describe who you're looking for, the challenges, and the opportunity."
                />
                <FlowStep
                  icon={UserCheck}
                  title="Receive direct applications"
                  description="Verified applicants apply directly on JOBUWIN — with video intros, not just CVs."
                />
                <FlowStep
                  icon={Filter}
                  title="No fake or AI noise"
                  description="Bot profiles and AI-generated spam are filtered out before they ever reach you."
                />
                <FlowStep
                  icon={LineChart}
                  title="Track applications in one place"
                  description="Every applicant lands in a single dashboard, so nothing slips through the cracks."
                />
                <FlowStep
                  icon={UserPlus}
                  title="Invite applicants"
                  description="See real signals — video intros and verified identity — alongside every profile."
                />
                <FlowStep
                  icon={Filter}
                  title="Filter & shortlist"
                  description="Sort and narrow your applicants down to the shortlist you actually want to talk to."
                />
                <FlowStep
                  icon={Video}
                  title="Watch the video pitch"
                  description="Hear, in their own words, how each applicant would help you deliver."
                />
                <FlowStep
                  icon={MessagesSquare}
                  title="Decide with confidence"
                  description="Compare pitches side by side and move the best people forward, faster."
                />
              </Nd1FlowBreakdown>
            </div>
          </div>

          <div className="nd1-section nd1-section--tight">
            <div className="nd1-js-cta-band">
              <span className="nd1-js-cta-band__icon">
                <UserRound className="nd1-icon" aria-hidden="true" />
              </span>
              <p className="nd1-js-cta-band__copy nd1-js-cta-band__copy--sm">
                <span className="nd1-ink-orange">Start hiring genuine people.</span> Post your first verified job
                and start meeting real candidates today.
              </p>
              <Link to="/signup?type=hiring-manager" className="nd1-btn nd1-btn--accent">
                Start hiring
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Nd1SiteFooter />
    </div>
  );
}
